import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import Layout from "../../component/layout";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";
import { addSuplierUrl } from "../../../../utils/urls"; 

const AddSupplier = () => {
  const navigate = useNavigate();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const facilityId = selectedFacilityId;

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contactPersonName, setContactPersonName] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [taxIdentificationNumber, setTaxIdentificationNumber] = useState("");

  // Dynamic documents handling
  const [documents, setDocuments] = useState([
    { documentName: "", documentType: "other", file: null }
  ]);

  const documentTypeOptions = [
    { label: "Pin Certificate", value: "pin" },
    { label: "Registration", value: "registration" },
    { label: "Incorporation", value: "incorporation" },
    { label: "ID", value: "id" },
    { label: "Other", value: "other" }
  ];

  const [isSubmitting, setIsSubmitting] = useState(false);

  // If no facility selected, prompt user
  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="text-center p-5">
          Please select a facility first.
        </div>
      </Layout>
    );
  }

  // Handle document name change
  const handleDocumentNameChange = (index, value) => {
    const updatedDocuments = [...documents];
    updatedDocuments[index].documentName = value;
    setDocuments(updatedDocuments);
  };

  // Handle document type change
  const handleDocumentTypeChange = (index, value) => {
    const updatedDocuments = [...documents];
    updatedDocuments[index].documentType = value;
    setDocuments(updatedDocuments);
  };

  // Handle document file change
  const handleDocumentFileChange = (index, file) => {
    const updatedDocuments = [...documents];
    updatedDocuments[index].file = file;
    setDocuments(updatedDocuments);
  };

  // Add new document field
  const addDocumentField = () => {
    setDocuments([
      ...documents,
      { documentName: "", documentType: "other", file: null }
    ]);
  };

  // Remove document field
  const removeDocumentField = (index) => {
    const updatedDocuments = [...documents];
    updatedDocuments.splice(index, 1);
    setDocuments(updatedDocuments);
  };

  // Clear form
  const handleClear = () => {
    setName("");
    setEmail("");
    setPhone("");
    setContactPersonName("");
    setDepartmentName("");
    setTaxIdentificationNumber("");
    
    // Reset to single empty document field
    setDocuments([{ documentName: "", documentType: "other", file: null }]);
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      // Basic validation
      if (!name.trim()) {
        toastify("Supplier Name is required", "error");
        return;
      }

      if (!email.trim()) {
        toastify("Email is required", "error");
        return;
      }

      if (!phone.trim()) {
        toastify("Phone number is required", "error");
        return;
      }

      // Validate documents
      for (let i = 0; i < documents.length; i++) {
        if (documents[i].file && !documents[i].documentName.trim()) {
          toastify(`Please provide a name for document ${i + 1}`, "error");
          return;
        }
      }

      setIsSubmitting(true);

      // Prepare form data for file uploads (multipart/form-data)
      const formData = new FormData();
      formData.append("name", name);
      formData.append("email", email);
      formData.append("phone", phone);
      formData.append("contactPerson[name]", contactPersonName);
      formData.append("department[name]", departmentName);
      formData.append("taxIdentificationNumber", taxIdentificationNumber);

      // Append only documents that have files
      let documentCount = 0;
      documents.forEach((doc, index) => {
        if (doc.file && doc.documentName.trim()) {
          // Change this line
          formData.append(`document_${documentCount}`, doc.file);
          formData.append(`documentName_${documentCount}`, doc.documentName);
          formData.append(`documentType_${documentCount}`, doc.documentType);
          documentCount++;
        }
      });

      // Use the imported URL and append facilityId to it
      const response = await makeRequest2(
        `${addSuplierUrl}/${facilityId}`,
        "POST",
        formData,
        { "Content-Type": "multipart/form-data" }
      );

      if (!response.success) {
        toastify(response.error || "Failed to add supplier", "error");
        setIsSubmitting(false);
        return;
      }

      toastify(response.message || "Supplier added successfully", "success");
      handleClear();
      // Navigate back to the supplier listing
      navigate("/facility/procurement_management/supplier_management");
    } catch (error) {
      toastify(
        "An unexpected error occurred: " + (error.message || "Unknown error"),
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };
 
  return (
    <Layout>
      {/* Page Header & Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/procurement_management/supplier_management">
                    Suppliers
                  </Link>
                </li>
                <li className="breadcrumb-item">Add New Supplier</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main form card */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              {/* Back Link */}
              <Link to="/facility/procurement_management/supplier_management">
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5>Add New Supplier</h5>
            </div>
            <div className="card-body">
              <div className="container">
                <div className="row">
                  {/* Supplier Name */}
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">
                      Supplier Name<span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter supplier name"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Email Address */}
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">
                      Email Address<span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter email address"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">
                      Phone Number<span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      className="form-control"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Enter phone number"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Tax Identification Number */}
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">Tax Identification Number</label>
                    <InputText
                      className="form-control"
                      value={taxIdentificationNumber}
                      onChange={(e) => setTaxIdentificationNumber(e.target.value)}
                      placeholder="Enter tax identification number"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Contact Person Name */}
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">Contact Person Name</label>
                    <InputText
                      className="form-control"
                      value={contactPersonName}
                      onChange={(e) => setContactPersonName(e.target.value)}
                      placeholder="Enter contact person name"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Department Name */}
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">Department Name</label>
                    <InputText
                      className="form-control"
                      value={departmentName}
                      onChange={(e) => setDepartmentName(e.target.value)}
                      placeholder="Enter department name"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Documents Section */}
                  <div className="col-sm-12 mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-3">Documents</h6>
                      <Button
                        type="button"
                        icon="ti ti-plus"
                        className="p-button-sm p-button-outlined"
                        onClick={addDocumentField}
                        disabled={isSubmitting}
                        tooltip="Add Document"
                      />
                    </div>
                  </div>

                  {/* Dynamic Document Uploads */}
                  {documents.map((doc, index) => (
                    <div key={index} className="col-12 mb-3">
                      <div className="card p-3">
                        <div className="row g-2">
                          <div className="col-md-5">
                            <label className="form-label">Document Name</label>
                            <InputText
                              className="form-control"
                              value={doc.documentName}
                              onChange={(e) => handleDocumentNameChange(index, e.target.value)}
                              placeholder="Enter document name"
                              disabled={isSubmitting}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Document Type</label>
                            <Dropdown
                              value={doc.documentType}
                              options={documentTypeOptions}
                              onChange={(e) => handleDocumentTypeChange(index, e.value)}
                              className="w-100"
                              disabled={isSubmitting}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">File</label>
                            <input
                              type="file"
                              className="form-control"
                              onChange={(e) => handleDocumentFileChange(index, e.target.files[0])}
                              disabled={isSubmitting}
                            />
                          </div>
                          <div className="col-md-1 d-flex align-items-end">
                            {documents.length > 1 && (
                              <Button
                                type="button"
                                icon="ti ti-trash"
                                className="p-button-sm p-button-danger p-button-outlined"
                                onClick={() => removeDocumentField(index)}
                                disabled={isSubmitting}
                                tooltip="Remove"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Buttons */}
              <div className="text-end mt-4">
                <Button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClear}
                  disabled={isSubmitting}
                >
                  Clear
                </Button>
                &nbsp;
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddSupplier;